import React, { useState, useEffect, useRef } from "react";
import { useToast } from "../toast/ToastContext";
import FuelLoading from "../../pages/FuelLoading";
import {
  FaPlus,
  FaTrash,
  FaPen,
  FaCheck,
  FaToggleOn,
  FaToggleOff,
  FaMapMarkerAlt,
  FaVideo,
  FaCalendarAlt,
  FaLock,
  FaGlobe,
} from "react-icons/fa";
import "./adminItinerary.scss"; // Match your scss structural sheet names
import ConfirmModal from "../modal/ConfirmModal";
import AdminHeader from "../layout/AdminHeader";
import {
  useCreateItineraryMutation,
  useDeleteItineraryMutation,
  useGetItinerariesQuery,
  useUpdateItineraryMutation,
} from "../../features/api/itineraryApiSlice";

export default function AdminItinerary() {
  const { showToast } = useToast();

  // CORE API READ/WRITE OPERATIONS MATRIX
  const { data: trips = [], isLoading, isError } = useGetItinerariesQuery();
  const [createItinerary, { isLoading: isCreating }] =
    useCreateItineraryMutation();
  const [updateItinerary, { isLoading: isUpdating }] =
    useUpdateItineraryMutation();
  const [deleteItineraryMutation, { isLoading: isDeletingItinerary }] =
    useDeleteItineraryMutation();

  // 🟢 2. HANDLE APISLICE CHECKS OR LOGICAL FALLBACKS USING STANDARD CONSTANTS
  const deleteItinerary = deleteItineraryMutation;
  const isDeleting = isDeletingItinerary;

  const isMutating = isCreating || isUpdating || isDeleting;

  // PRIMARY WORKSPACE CONFIGURATION FIELDS
  const [editingId, setEditingId] = useState(null);
  const [tripTitle, setTripTitle] = useState("");
  const [targetStates, setTargetStates] = useState("");
  const [estimatedDate, setEstimatedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("Planned");
  const [isPublic, setIsPublic] = useState(false);

  // NESTED TRACKING ARRAY BUFFER FIELDS
  const [routeStops, setRouteStops] = useState([]);
  const [stopInput, setStopInput] = useState("");
  const [stopNotesInput, setStopNotesInput] = useState("");
  const [videoConcepts, setVideoConcepts] = useState([]);
  const [videoInput, setVideoInput] = useState("");

  // CUSTOM UN-BLUE UN-NATIVE STATUS SELECTOR STATES
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const dropdownRef = useRef(null);

  // MODAL INTERACTIVE PURGE TRACKERS
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  // Handle dropdown outside click closures
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsStatusOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const resetFormWorkspace = () => {
    setEditingId(null);
    setTripTitle("");
    setTargetStates("");
    setEstimatedDate("");
    setNotes("");
    setStatus("Planned");
    setIsPublic(false);
    setRouteStops([]);
    setVideoConcepts([]);
    setStopInput("");
    setStopNotesInput("");
    setVideoInput("");
  };

  const handleEditInit = (trip) => {
    setEditingId(trip._id);
    setTripTitle(trip.tripTitle || "");
    setTargetStates(trip.targetStates?.join(", ") || "");
    setEstimatedDate(trip.estimatedDate || "");
    setNotes(trip.notes || "");
    setStatus(trip.status || "Planned");
    setIsPublic(trip.isPubliclyVisible || false); // 🟢 FIXED: Reads from the correct backend key
    setRouteStops(trip.routeStops || []);
    setVideoConcepts(trip.videoConcepts || []);
  };

  const appendStopLocal = () => {
    if (!stopInput.trim()) return;
    setRouteStops([
      ...routeStops,
      { stopName: stopInput.trim(), notes: stopNotesInput.trim() },
    ]);
    setStopInput("");
    setStopNotesInput("");
  };

  const removeStopLocal = (idx) => {
    setRouteStops(routeStops.filter((_, i) => i !== idx));
  };

  const appendVideoLocal = () => {
    if (!videoInput.trim()) return;
    setVideoConcepts([
      ...videoConcepts,
      { ideaTitle: videoInput.trim(), isFilmed: false },
    ]);
    setVideoInput("");
  };

  const removeVideoLocal = (idx) => {
    setVideoConcepts(videoConcepts.filter((_, i) => i !== idx));
  };

  const toggleVideoFilmedState = (idx) => {
    const updated = [...videoConcepts];
    updated[idx] = { ...updated[idx], isFilmed: !updated[idx].isFilmed };
    setVideoConcepts(updated);
  };

  const handleDeleteTrigger = (id, title) => {
    setModalConfig({ isOpen: true, targetId: id, targetTitle: title });
  };

  const executePurgeTripPlan = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });
    try {
      await deleteItinerary(targetId).unwrap();
      showToast(`"${targetTitle}" itinerary purged from records.`, "success");
      if (editingId === targetId) resetFormWorkspace();
    } catch (err) {
      showToast("Failed to remove target route from database.", "error");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!tripTitle.trim()) {
      showToast("A clear Trip Adventure Title is required.", "error");
      return;
    }

    // 🟢 Construct payload using the exact schema key name
    const payload = {
      tripTitle: tripTitle.trim(),
      targetStates: targetStates
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      estimatedDate: estimatedDate.trim(),
      routeStops,
      videoConcepts,
      status,
      isPubliclyVisible: isPublic, // 🟢 FIXED: Maps frontend 'isPublic' state to backend 'isPubliclyVisible' key
      notes: notes.trim(),
    };

    try {
      if (editingId) {
        await updateItinerary({ id: editingId, ...payload }).unwrap();
        showToast(
          "Trip parameters modified and synchronized cleanly!",
          "success"
        );
      } else {
        await createItinerary(payload).unwrap();
        showToast("New map itinerary locked into upcoming records!", "success");
      }
      resetFormWorkspace();
    } catch (err) {
      showToast("Failed to write data packet down pipeline channels.", "error");
    }
  };

  if (isLoading)
    return (
      <div className="admin-planner-fallback">
        <FuelLoading text="Opening timeline tracks..." />
      </div>
    );
  if (isError)
    return (
      <div className="admin-planner-fallback">
        ⚠️ Terminal authentication error or database is offline.
      </div>
    );

  return (
    <div className="admin-itinerary-workspace fuel-fade-in">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        variant="warning"
        title="Purge Scheduled Itinerary"
        confirmText="Erase Route"
        message={`Are you completely sure you want to delete the plan map tracking "${modalConfig.targetTitle}"? This structural bypass cannot be undone.`}
        onConfirm={executePurgeTripPlan}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />
      {isMutating && (
        <FuelLoading text="Writing configurations to database..." fullScreen />
      )}

      <AdminHeader
        title="Adventure Planner & Itinerary Board"
        description="Map out next video route trajectories, coordinate waypoint stops, and plan upcoming video content checks."
        badgeText="Logistics Desk"
      />

      <div className="planner-layout-columns-grid">
        {/* LEFT COLUMN: INTERACTIVE COMPOSITION EDITOR SECTION */}
        <section className="planner-composer-card">
          <h2>
            {editingId ? "✏️ Adjust Planned Route" : "Outline Next Destination"}
          </h2>
          <p>
            Chart map segments, log private operational constraints, and track
            production concepts.
          </p>

          <form onSubmit={handleFormSubmit} className="itinerary-core-form">
            <div className="form-input-row halves">
              <div className="field-group">
                <label>Trip Adventure Title *</label>
                <input
                  type="text"
                  value={tripTitle}
                  onChange={(e) => setTripTitle(e.target.value)}
                  placeholder="e.g., Badlands to Black Hills"
                  required
                />
              </div>
              <div className="field-group">
                <label>Estimated Horizon Date</label>
                <input
                  type="text"
                  value={estimatedDate}
                  onChange={(e) => setEstimatedDate(e.target.value)}
                  placeholder="e.g., Fall 2026"
                />
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Target States (Comma Separated)</label>
                <input
                  type="text"
                  value={targetStates}
                  onChange={(e) => setTargetStates(e.target.value)}
                  placeholder="e.g., South Dakota, Wyoming"
                />
              </div>

              {/* CUSTOM UN-BLUE THEMED STATUS SELECTOR ENGINE */}
              <div className="field-group">
                <label>Operations Status</label>
                <div className="mini-miles-custom-select" ref={dropdownRef}>
                  <div
                    className={`select-trigger-box ${
                      isStatusOpen ? "active" : ""
                    }`}
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                  >
                    <span>⚡ {status}</span>
                    <span>▼</span>
                  </div>
                  {isStatusOpen && (
                    <ul className="select-options-tray">
                      <li
                        className={status === "Planned" ? "selected" : ""}
                        onClick={() => {
                          setStatus("Planned");
                          setIsStatusOpen(false);
                        }}
                      >
                        Planned
                      </li>
                      <li
                        className={status === "In Progress" ? "selected" : ""}
                        onClick={() => {
                          setStatus("In Progress");
                          setIsStatusOpen(false);
                        }}
                      >
                        In Progress
                      </li>
                      <li
                        className={status === "Completed" ? "selected" : ""}
                        onClick={() => {
                          setStatus("Completed");
                          setIsStatusOpen(false);
                        }}
                      >
                        Completed
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* 🟢 DYNAMIC WAYPOINT GEOGRAPHY BUILDER SUB-FORM */}
            <div className="nested-array-builder-box">
              <label>Coordinate Route Waypoint Stops</label>
              <div className="input-append-row stack-vertical-mobile">
                <input
                  type="text"
                  value={stopInput}
                  onChange={(e) => setStopInput(e.target.value)}
                  placeholder="Stop Location name..."
                />
                <input
                  type="text"
                  value={stopNotesInput}
                  onChange={(e) => setStopNotesInput(e.target.value)}
                  placeholder="Optional waypoint notes..."
                />
                <button
                  type="button"
                  className="btn-inner-add"
                  onClick={appendStopLocal}
                >
                  <FaPlus />
                </button>
              </div>

              {/* Local Waypoints Sub-List Matrix Arrays Wrapper */}
              {routeStops.length > 0 && (
                <ul className="local-preview-list">
                  {routeStops.map((stop, idx) => (
                    <li key={idx} className="preview-list-item">
                      <div className="item-text-block">
                        <strong>📍 {stop.stopName}</strong>
                        {stop.notes && (
                          <span className="sub-notes-text">
                            {" "}
                            — {stop.notes}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-remove-pill"
                        onClick={() => removeStopLocal(idx)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 🟢 DYNAMIC VIDEO CONTENT DECK PLANNER ARRAY SUB-FORM */}
            <div className="nested-array-builder-box">
              <label>Outline Video Content Production Concepts</label>
              <div className="input-append-row">
                <input
                  type="text"
                  value={videoInput}
                  onChange={(e) => setVideoInput(e.target.value)}
                  placeholder="e.g., Small car roof rack fuel economy test..."
                />
                <button
                  type="button"
                  className="btn-inner-add"
                  onClick={appendVideoLocal}
                >
                  <FaPlus />
                </button>
              </div>

              {/* Local Video Concepts Production Checklist Matrix Arrays Wrapper */}
              {videoConcepts.length > 0 && (
                <ul className="local-preview-list">
                  {videoConcepts.map((vid, idx) => (
                    <li
                      key={idx}
                      className={`preview-list-item ${
                        vid.isFilmed ? "item--checked" : ""
                      }`}
                    >
                      <label className="checkbox-item-label">
                        <input
                          type="checkbox"
                          checked={vid.isFilmed}
                          onChange={() => toggleVideoFilmedState(idx)}
                        />
                        <span>🎬 {vid.ideaTitle}</span>
                      </label>
                      <button
                        type="button"
                        className="btn-remove-pill"
                        onClick={() => removeVideoLocal(idx)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* PRIVATE INTERNAL TEXT LOG AREA */}
            <div className="field-group">
              <label>Private Operations Notes Summary</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Log logistical details, gear constraints, or channel scheduling limits here..."
              />
            </div>

            {/* METRICS CHASSIS VISIBILITY OPTIONS FOOTER CONTROL BAR */}
            <footer className="form-action-footer-bar">
              <div className="status-toggle-wrapper">
                <label className="checkbox-toggle">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label-text">
                    {isPublic
                      ? "🟢 Sync to Public Upcoming Trips Widget"
                      : "📁 Private Administrative Plan"}
                  </span>
                </label>
              </div>

              <div className="buttons-cluster">
                {editingId && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={resetFormWorkspace}
                    disabled={isMutating}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="fuel-primary-btn publish-itinerary-btn"
                  disabled={isMutating}
                >
                  {editingId
                    ? "Save Route Overwrites"
                    : "Lock Route into Planner"}
                </button>
              </div>
            </footer>
          </form>
        </section>

        {/* ===== RIGHT COLUMN: THE ROUTE MATRIX DATA SHEETS FEED LIST ===== */}
        <section className="planner-library-section">
          <div className="library-panel-header">
            <h3>Active Planned Log Books ({trips.length})</h3>
          </div>

          <div className="itineraries-scroll-stack">
            {trips.length === 0 ? (
              <p className="empty-planner-text">
                No upcoming travel tracks plotted inside the database ledger
                yet.
              </p>
            ) : (
              trips.map((trip) => (
                <div
                  key={trip._id}
                  className={`trip-row-card ${
                    editingId === trip._id ? "trip-row-card--active" : ""
                  }`}
                >
                  <div className="trip-row-card__meta">
                    <div className="card-title-row">
                      <strong>{trip.tripTitle}</strong>
                      <span
                        className={`status-tag status-tag--${trip.status
                          .toLowerCase()
                          .replace(/\s+/g, "")}`}
                      >
                        {trip.status}
                      </span>
                      {trip.isPublic && (
                        <span className="public-widget-badge">Live Widget</span>
                      )}
                    </div>

                    <div className="card-sub-details">
                      {trip.estimatedDate && (
                        <span>📅 {trip.estimatedDate}</span>
                      )}
                      {trip.targetStates?.length > 0 && (
                        <span className="states-list">
                          📍 {trip.targetStates.join(", ")}
                        </span>
                      )}
                    </div>

                    {/* Dynamic sub-counts overview metrics snippets logs for quick scanning info */}
                    <div className="card-counts-row">
                      <span>🗺️ {trip.routeStops?.length || 0} Waypoints</span>
                      <span>
                        🎥 {trip.videoConcepts?.length || 0} Video Tasks
                      </span>
                    </div>
                  </div>

                  <div className="trip-row-card__controls">
                    <button
                      type="button"
                      className="ctrl-action-btn edit"
                      onClick={() => handleEditInit(trip)}
                      disabled={isMutating}
                    >
                      <FaPen /> Edit
                    </button>
                    <button
                      type="button"
                      className="ctrl-action-btn delete"
                      onClick={() =>
                        handleDeleteTrigger(trip._id, trip.tripTitle)
                      }
                      disabled={isMutating}
                    >
                      <FaTrash /> Purge
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
